import { PostalResponse } from '@app-model/api';
import { CartItem, CartTaxFormValues } from '@app-model/cart';
import { CheckoutFormValues, SubmittedCartItem } from '@app-model/checkout';
import { Forms } from '@app-model/form';

import * as CartActions from './cart.actions';


export interface State {
  addCartContainsError: boolean;
  cart: CartItem[];
  cartCostContainsError: boolean;
  cartTaxFormValues: CartTaxFormValues[];
  checkoutCartContainsError: boolean;
  checkoutForms: Forms;
  checkoutFormValues: CheckoutFormValues;
  cosignerFormEnable: boolean;
  cosignerPostal: PostalResponse;
  customerPostal: PostalResponse;
  error: string;
  savedQuote: boolean;
  submittedCart: SubmittedCartItem[];
  submittedCartSuccess: boolean;
  success: boolean;
  total: number;
  validatedCart: CartItem[];
}

const InitialState: State = {
  addCartContainsError: false,
  cart: [],
  cartCostContainsError: false,
  cartTaxFormValues: [],
  checkoutCartContainsError: false,
  checkoutForms: new Forms(),
  checkoutFormValues: new CheckoutFormValues(),
  cosignerFormEnable: false,
  cosignerPostal: new PostalResponse(),
  customerPostal: new PostalResponse(),
  error: '',
  savedQuote: false,
  submittedCart: [],
  submittedCartSuccess: false,
  success: false,
  total: 0.0,
  validatedCart: [],
};

export function reducer(state = InitialState, action: CartActions.All): State {
  switch (action.type) {
    case CartActions.ADD_CART: {
      return {
        ...state,
        cart: [...state.cart, JSON.parse(JSON.stringify(action.payload))],
        savedQuote: false,
      };
    }

    case CartActions.DELETE_CART: {
      return {
        ...state,
        cart: state.cart.filter((item) => item.key !== action.payload.key),
        cartTaxFormValues: state.cartTaxFormValues.filter((item) => item.key !== action.payload.key),
        savedQuote: false,
      };
    }

    case CartActions.UPDATE_CHECKOUT_FORM: {
      return { ...state, checkoutFormValues: action.payload };
    }

    case CartActions.ADD_TAXES_SUCCESS: {
      return { ...state, validatedCart: action.payload };
    }

    case CartActions.UPDATE_TAX_FORM_VALUES: {
      return { ...state, cartTaxFormValues: action.payload };
    }

    case CartActions.GET_CHECKOUT_FORM_SUCCESS: {
      return {
        ...state,
        checkoutForms: action.payload,
        submittedCartSuccess: false,
      };
    }

    case CartActions.SUBMIT_CART_SUCCESS: {
      return {
        ...state,
        cart: action.payload.invalidCart,
        validatedCart: action.payload.invalidCart,
        submittedCart: [...state.submittedCart, ...action.payload.submittedCart],
        savedQuote: false,
        submittedCartSuccess: action.payload.submittedCart.length > 0,
        checkoutCartContainsError: action.payload.invalidCart.length > 0,
      };
    }

    case CartActions.TOGGLE_COSIGNER_FORM: {
      return { ...state, cosignerFormEnable: action.payload };
    }

    case CartActions.GET_CUSTOMER_POSTAL_SUCCESS: {
      return { ...state, customerPostal: action.payload };
    }

    case CartActions.GET_COSIGNER_POSTAL_SUCCESS: {
      return { ...state, cosignerPostal: action.payload };
    }

    case CartActions.VALIDATE_CART_SUCCESS: {
      return {
        ...state,
        validatedCart: action.payload.validatedCart.validatedCart,
        success: true,
        addCartContainsError: action.payload.validatedCart.containsError,
        savedQuote: false,
      };
    }

    case CartActions.LOAD_QUOTE_SUCCESS: {
      return {
        ...state,
        validatedCart: action.payload.cartState.validatedCart,
        cart: action.payload.cartState.cart,
        submittedCart: action.payload.cartState.submittedCart,
        total: action.payload.cartState.total,
        success: action.payload.cartState.success,
        error: action.payload.cartState.error,
        checkoutFormValues: action.payload.cartState.checkoutFormValues,
        customerPostal: action.payload.cartState.customerPostal,
        cosignerPostal: action.payload.cartState.cosignerPostal,
        cosignerFormEnable: action.payload.cartState.cosignerFormEnable,
        checkoutForms: action.payload.cartState.checkoutForms,
        submittedCartSuccess: false,
        addCartContainsError: action.payload.cartState.addCartContainsError,
        checkoutCartContainsError: false,
        savedQuote: true,
        // use conditional operator for objects that may not be in load quote
        cartCostContainsError: action.payload.cartState['cartCostContainsError']
          ? action.payload.cartState['cartCostContainsError']
          : false,
        cartTaxFormValues: action.payload.cartState['cartTaxFormValues']
          ? action.payload.cartState['cartTaxFormValues']
          : [],
      };
    }

    case CartActions.RESET_CART: {
      return {
        cart: [],
        validatedCart: [],
        submittedCart: [],
        cartTaxFormValues: [],
        checkoutForms: new Forms(),
        total: 0.0,
        error: '',
        success: false,
        savedQuote: false,
        submittedCartSuccess: false,
        addCartContainsError: false,
        cartCostContainsError: false,
        checkoutCartContainsError: false,
        customerPostal: new PostalResponse(),
        cosignerPostal: new PostalResponse(),
        checkoutFormValues: new CheckoutFormValues(),
        cosignerFormEnable: false,
      };
    }

    case CartActions.CART_FAILURE: {
      return {
        ...state,
        success: false,
        error: action.payload,
        savedQuote: false,
      };
    }

    default: {
      return { ...state };
    }
  }
}
